import CompareIcon from '@mui/icons-material/Compare';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import SpeedIcon from '@mui/icons-material/Speed';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PageTitle from 'components/UI/PageTitle';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetAdminBusinessUnitsQuery } from 'services/api/adminBusinessUnits.api';
import {
  useDeleteConversationEvalMutation,
  useGetAllConversationEvalsQuery,
  useRunBlindComparisonEvalMutation,
  useRunConversationEvalMutation,
  useRunSingleModelEvalMutation,
} from 'services/api/conversationEvals.api';
import { type ConversationEval, isRunConversationEvalError } from 'services/types/conversationEvals';

import BlindComparisonForm from './components/BlindComparisonForm';
import ConversationEvalForm from './components/ConversationEvalForm';
import RunEvaluationForm from './components/RunEvaluationForm';
import SingleModelForm from './components/SingleModelForm';
import ViewConversationEval from './components/ViewConversationEval';

const ConversationEvals: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedBusinessUnitIds, setSelectedBusinessUnitIds] = useState<number[]>([]);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(!!searchParams.get('messageId'));
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedEval, setSelectedEval] = useState<ConversationEval | null>(null);
  const [openRunDialog, setOpenRunDialog] = useState(false);
  const [openRunEvalDialog, setOpenRunEvalDialog] = useState(false);
  const [selectedEvalForRun, setSelectedEvalForRun] = useState<ConversationEval | null>(null);
  const [openBlindComparisonDialog, setOpenBlindComparisonDialog] = useState(false);
  const [selectedEvalForBlindComparison, setSelectedEvalForBlindComparison] = useState<ConversationEval | null>(null);
  const [openSingleModelDialog, setOpenSingleModelDialog] = useState(false);
  const [selectedEvalForSingleModel, setSelectedEvalForSingleModel] = useState<ConversationEval | null>(null);

  const { data: businessUnits } = useGetAdminBusinessUnitsQuery(null);
  const { data: conversationEvals, refetch } = useGetAllConversationEvalsQuery(
    selectedBusinessUnitIds.length ? { businessUnitIds: selectedBusinessUnitIds } : undefined
  );
  const [deleteConversationEval] = useDeleteConversationEvalMutation();
  const [
    runConversationEval,
    { isLoading: isRunningEval, data: runConversationEvalData, error: runConversationEvalError },
  ] = useRunConversationEvalMutation();
  const [
    runBlindComparisonEval,
    { isLoading: isRunningBlindComparison, data: blindComparisonData, error: blindComparisonError },
  ] = useRunBlindComparisonEvalMutation();
  const [runSingleModelEval, { isLoading: isRunningSingleModel, data: singleModelData, error: singleModelError }] =
    useRunSingleModelEvalMutation();

  const handleBusinessUnitChange = (event: SelectChangeEvent<number[]>) => {
    setSelectedBusinessUnitIds(event.target.value as number[]);
  };

  const removeQueryParams = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('messageId');
    setSearchParams(newSearchParams);
  };

  const handleOpenViewDialog = (conversationEval: ConversationEval) => {
    setSelectedEval(conversationEval);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setSelectedEval(null);
    // Drop the deep-link param, otherwise the effect that honors it reopens the dialog immediately.
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('evalId');
    setSearchParams(newSearchParams);
  };

  const handleOpenDeleteDialog = (conversationEval: ConversationEval) => {
    setSelectedEval(conversationEval);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedEval(null);
  };

  const handleOpenRunEvalDialog = (conversationEval: ConversationEval) => {
    setSelectedEvalForRun(conversationEval);
    setOpenRunEvalDialog(true);
  };

  const handleCloseRunEvalDialog = () => {
    setOpenRunEvalDialog(false);
    setSelectedEvalForRun(null);
  };

  const handleOpenBlindComparisonDialog = (conversationEval: ConversationEval) => {
    setSelectedEvalForBlindComparison(conversationEval);
    setOpenBlindComparisonDialog(true);
  };

  const handleCloseBlindComparisonDialog = () => {
    setOpenBlindComparisonDialog(false);
    setSelectedEvalForBlindComparison(null);
  };

  const handleOpenSingleModelDialog = (conversationEval: ConversationEval) => {
    setSelectedEvalForSingleModel(conversationEval);
    setOpenSingleModelDialog(true);
  };

  const handleCloseSingleModelDialog = () => {
    setOpenSingleModelDialog(false);
    setSelectedEvalForSingleModel(null);
  };

  /**
   * Downloads blind comparison results as JSON file
   */
  const handleDownloadBlindComparisonResults = () => {
    if (!blindComparisonData) return;

    const dataStr = JSON.stringify(blindComparisonData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `blind-comparison-results-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   * Downloads single model evaluation results as JSON file
   */
  const handleDownloadSingleModelResults = () => {
    if (!singleModelData) return;

    const dataStr = JSON.stringify(singleModelData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `single-model-results-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDeleteConversationEval = async () => {
    if (selectedEval?.id) {
      try {
        await deleteConversationEval(selectedEval.id).unwrap();
        handleCloseDeleteDialog();
        await refetch();
      } catch (error) {
        console.error('Failed to delete conversation eval:', error);
      }
    }
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
    setSelectedEval(null);
    removeQueryParams();
    refetch().catch((error) => {
      console.error('Failed to refetch conversation evals:', error);
    });
  };

  const handleOpenEditDialog = (conversationEval: ConversationEval) => {
    setSelectedEval(conversationEval);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedEval(null);
    refetch().catch((error) => {
      console.error('Failed to refetch conversation evals:', error);
    });
  };

  // Deep link from the Playground after saving an eval: open it straight away instead of making the
  // user hunt for the row.
  useEffect(() => {
    const evalId = searchParams.get('evalId');
    if (!evalId || !conversationEvals) {
      return;
    }
    const target = conversationEvals.find((conversationEval) => conversationEval.id === Number(evalId));
    if (target) {
      setSelectedEval(target);
      setOpenViewDialog(true);
    }
  }, [searchParams, conversationEvals]);

  useEffect(() => {
    if (isRunningEval || !!runConversationEvalData || !!runConversationEvalError) {
      setOpenRunDialog(true);
    }
  }, [runConversationEvalData, runConversationEvalError, isRunningEval]);

  useEffect(() => {
    if (isRunningBlindComparison || !!blindComparisonData || !!blindComparisonError) {
      setOpenRunDialog(true);
    }
  }, [blindComparisonData, blindComparisonError, isRunningBlindComparison]);

  useEffect(() => {
    if (isRunningSingleModel || !!singleModelData || !!singleModelError) {
      setOpenRunDialog(true);
    }
  }, [singleModelData, singleModelError, isRunningSingleModel]);

  return (
    <Box sx={{ px: 2, pt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 2,
          alignItems: 'center',
          gap: 2,
        }}
      >
        <PageTitle>Conversation Evaluations</PageTitle>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
          }}
        >
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel id="business-unit-select-label">Business Units</InputLabel>
            <Select
              labelId="business-unit-select-label"
              multiple
              value={selectedBusinessUnitIds}
              onChange={handleBusinessUnitChange}
              label="Business Units"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const businessUnit = businessUnits?.find((bu) => bu.id === value);
                    return businessUnit ? <Chip key={value} label={businessUnit.name} size="small" /> : null;
                  })}
                </Box>
              )}
            >
              {businessUnits?.map((businessUnit) => (
                <MenuItem key={businessUnit.id} value={businessUnit.id}>
                  {businessUnit.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" color="primary" onClick={() => setOpenCreateDialog(true)}>
            Crear nuevo Chat Evaluation
          </Button>
        </Box>
      </Box>
      <TableContainer
        sx={{ height: 'calc(100vh - 100px)', overflow: 'auto', backgroundColor: 'white', borderRadius: 4, p: 2 }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Mock Client</TableCell>
              <TableCell>Business Units</TableCell>
              <TableCell>Cantidad de Mensajes</TableCell>
              <TableCell>Fecha de Creación</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {conversationEvals?.map((conversationEval) => (
              <TableRow key={conversationEval.id}>
                <TableCell>{conversationEval.id}</TableCell>
                <TableCell>
                  {`${conversationEval.mockClient.firstName || ''} ${
                    conversationEval.mockClient.lastName || ''
                  }`.trim() || 'N/A'}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {conversationEval.businessUnitIds.map((buId) => {
                      const businessUnit = businessUnits?.find((bu) => bu.id === buId);
                      return businessUnit ? (
                        <Chip key={buId} label={businessUnit.name} size="small" />
                      ) : (
                        <Chip key={buId} label={`BU ${buId}`} size="small" />
                      );
                    })}
                  </Box>
                </TableCell>
                <TableCell>{conversationEval.messages.length}</TableCell>
                <TableCell>{new Date(conversationEval.createdAt || '').toLocaleString()}</TableCell>
                <TableCell>
                  <Box
                    sx={{
                      display: 'flex',
                    }}
                  >
                    <Tooltip title="Ejecutar">
                      <IconButton onClick={() => handleOpenRunEvalDialog(conversationEval)}>
                        <PlayCircleIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Comparación Ciega">
                      <IconButton onClick={() => handleOpenBlindComparisonDialog(conversationEval)}>
                        <CompareIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Evaluación Modelo Único">
                      <IconButton onClick={() => handleOpenSingleModelDialog(conversationEval)}>
                        <SpeedIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Ver">
                      <IconButton onClick={() => handleOpenViewDialog(conversationEval)}>
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton onClick={() => handleOpenEditDialog(conversationEval)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton onClick={() => handleOpenDeleteDialog(conversationEval)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Create Dialog */}
      <Dialog open={openCreateDialog} onClose={handleCloseCreateDialog} maxWidth="md" fullWidth>
        <DialogTitle>Crear Nueva Chat Evaluation</DialogTitle>
        <DialogContent>
          <ConversationEvalForm
            onClose={handleCloseCreateDialog}
            messageId={searchParams.get('messageId') ? Number(searchParams.get('messageId')) : undefined}
          />
        </DialogContent>
      </Dialog>
      {/* View Dialog */}
      <Dialog open={openViewDialog} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogContent>{selectedEval && <ViewConversationEval conversationEval={selectedEval} />}</DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>
      {/* Edit Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>Editar Chat Evaluation</DialogTitle>
        <DialogContent>
          {selectedEval && <ConversationEvalForm conversationEval={selectedEval} onClose={handleCloseEditDialog} />}
        </DialogContent>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Eliminar Chat Evaluation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea eliminar esta evaluación de conversación? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancelar</Button>
          <Button
            onClick={() => {
              handleDeleteConversationEval().catch((e) => {
                console.error('Failed to delete conversation eval:', e);
              });
            }}
            color="error"
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
      {/* Run Dialog */}
      <Dialog open={openRunDialog} onClose={() => setOpenRunDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            borderBottom: '1px solid #e0e0e0',
            pb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <PlayCircleIcon color="primary" />
          Ejecutar Chat Evaluation
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {(isRunningEval || isRunningBlindComparison || isRunningSingleModel) && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 4,
              }}
            >
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {isRunningBlindComparison && 'Ejecutando comparación ciega...'}
                {isRunningSingleModel && 'Ejecutando evaluación de modelo único...'}
                {!isRunningBlindComparison && !isRunningSingleModel && 'Ejecutando evaluación...'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Esto puede tomar unos momentos
              </Typography>
            </Box>
          )}

          {isRunConversationEvalError(runConversationEvalError) && (
            <Box>
              <Typography variant="h6" color="error" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <DeleteIcon />
                Evals fallidos
              </Typography>
              {Object.entries(runConversationEvalError.data || {})
                .map(([key, failure]: [string, unknown], index) => {
                  const typedFailure = failure as {
                    alignmentScore?: number;
                    evalData?: { expected: unknown };
                    response?: unknown;
                  };
                  if (key.startsWith('failure_') && failure) {
                    return (
                      <Box
                        key={index}
                        sx={{
                          backgroundColor: '#fff3f3',
                          border: '1px solid #ffcdd2',
                          borderRadius: 2,
                          p: 3,
                          mb: 2,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                      >
                        <Typography
                          variant="h6"
                          color="error"
                          sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <DeleteIcon fontSize="small" />
                          {key.replace('failure_', 'Fallo ')}
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <Chip
                            label={`Alignment Score: ${typedFailure?.alignmentScore || 'N/A'}`}
                            color="error"
                            variant="outlined"
                            size="small"
                          />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: 'text.secondary',
                              mb: 1,
                            }}
                          >
                            Respuesta Esperada:
                          </Typography>
                          <Box
                            sx={{
                              backgroundColor: '#f5f5f5',
                              p: 2,
                              borderRadius: 1,
                              border: '1px solid #e0e0e0',
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                              maxHeight: 200,
                              overflow: 'auto',
                            }}
                          >
                            {JSON.stringify(typedFailure?.evalData?.expected || {}, null, 2)}
                          </Box>
                        </Box>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: 'text.secondary',
                              mb: 1,
                            }}
                          >
                            Respuesta Actual:
                          </Typography>
                          <Box
                            sx={{
                              backgroundColor: '#f5f5f5',
                              p: 2,
                              borderRadius: 1,
                              border: '1px solid #e0e0e0',
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                              maxHeight: 200,
                              overflow: 'auto',
                            }}
                          >
                            {JSON.stringify(typedFailure?.response || {}, null, 2)}
                          </Box>
                        </Box>
                      </Box>
                    );
                  }
                  return null;
                })
                .filter(Boolean)}
            </Box>
          )}

          {blindComparisonData && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CompareIcon color="primary" />
                  Resultados de Comparación Ciega
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadBlindComparisonResults}
                >
                  Descargar JSON
                </Button>
              </Box>
              <pre style={{ overflow: 'auto', maxHeight: '400px', fontSize: '12px' }}>
                {JSON.stringify(blindComparisonData, null, 2)}
              </pre>
            </Box>
          )}

          {Boolean(singleModelData) && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SpeedIcon color="primary" />
                  Resultados de Evaluación de Modelo Único
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadSingleModelResults}
                >
                  Descargar JSON
                </Button>
              </Box>
              <pre style={{ overflow: 'auto', maxHeight: '400px', fontSize: '12px' }}>
                {JSON.stringify(singleModelData, null, 2)}
              </pre>
            </Box>
          )}

          {runConversationEvalData && (
            <Box>
              <Box
                sx={{
                  backgroundColor: '#f8f9fa',
                  borderRadius: 2,
                  p: 3,
                  mb: 3,
                  border: '1px solid #e9ecef',
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PlayCircleIcon color="success" />
                  Ejecución Completada
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    gap: 3,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      Mensajes Evaluados
                    </Typography>
                    <Typography variant="h6">
                      {runConversationEvalData?.successCount || 0} de {runConversationEvalData?.total || 0}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      Tasa de Éxito
                    </Typography>
                    <Typography
                      variant="h6"
                      color={
                        runConversationEvalData?.successCount === runConversationEvalData?.total
                          ? 'success.main'
                          : 'warning.main'
                      }
                    >
                      {runConversationEvalData?.total
                        ? `${(
                            ((runConversationEvalData.successCount || 0) / runConversationEvalData.total) * 100
                          ).toFixed(1)}%`
                        : '0%'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {runConversationEvalData?.failures?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" color="error" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DeleteIcon />
                    Evals Fallidos ({runConversationEvalData.failures.length})
                  </Typography>
                  {runConversationEvalData.failures.map((failure, index) => (
                    <Box
                      key={index}
                      sx={{
                        backgroundColor: '#fff3f3',
                        border: '1px solid #ffcdd2',
                        borderRadius: 2,
                        p: 3,
                        mb: 2,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      }}
                    >
                      <Box sx={{ mb: 2 }}>
                        <Chip
                          label={`Alignment Score: ${failure?.alignmentScore || 'N/A'}`}
                          color="error"
                          variant="outlined"
                          size="small"
                        />
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: 'text.secondary',
                            mb: 1,
                          }}
                        >
                          Respuesta Esperada:
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: '#f5f5f5',
                            p: 2,
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            maxHeight: 200,
                            overflow: 'auto',
                          }}
                        >
                          {JSON.stringify(failure?.evalData?.expected || {}, null, 2)}
                        </Box>
                      </Box>

                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: 'text.secondary',
                            mb: 1,
                          }}
                        >
                          Respuesta Actual:
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: '#f5f5f5',
                            p: 2,
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            maxHeight: 200,
                            overflow: 'auto',
                          }}
                        >
                          {JSON.stringify(failure?.response || {}, null, 2)}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {runConversationEvalData?.successes?.length > 0 && (
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'success.main',
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <PlayCircleIcon />
                    Evals Exitosos ({runConversationEvalData.successes.length})
                  </Typography>
                  {runConversationEvalData.successes.map((data, index) => (
                    <Box
                      key={index}
                      sx={{
                        backgroundColor: '#f1f8e9',
                        border: '1px solid #c8e6c9',
                        borderRadius: 2,
                        p: 3,
                        mb: 2,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      }}
                    >
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: 'text.secondary',
                            mb: 1,
                          }}
                        >
                          Respuesta Esperada:
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: '#f5f5f5',
                            p: 2,
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            maxHeight: 200,
                            overflow: 'auto',
                          }}
                        >
                          {JSON.stringify(data?.evalData?.expected || {}, null, 2)}
                        </Box>
                      </Box>

                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: 'text.secondary',
                            mb: 1,
                          }}
                        >
                          Respuesta Actual:
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: '#f5f5f5',
                            p: 2,
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            maxHeight: 200,
                            overflow: 'auto',
                          }}
                        >
                          {JSON.stringify(data?.response || {}, null, 2)}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => setOpenRunDialog(false)} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openRunEvalDialog} onClose={handleCloseRunEvalDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Ejecutar Evaluación</DialogTitle>
        <DialogContent>
          <RunEvaluationForm
            conversationEval={selectedEvalForRun}
            onClose={handleCloseRunEvalDialog}
            onRunEvaluation={(params) => {
              if (selectedEvalForRun) {
                runConversationEval({
                  conversationEvalIds: [selectedEvalForRun.id],
                  ...params,
                }).catch((e) => {
                  console.error('Failed to run conversation eval:', e);
                });
                handleCloseRunEvalDialog();
              }
            }}
          />
        </DialogContent>
      </Dialog>
      {/* Blind Comparison Dialog */}
      <Dialog open={openBlindComparisonDialog} onClose={handleCloseBlindComparisonDialog} maxWidth="lg" fullWidth>
        <DialogTitle>Comparación Ciega de Modelos</DialogTitle>
        <DialogContent>
          <BlindComparisonForm
            conversationEval={selectedEvalForBlindComparison}
            onClose={handleCloseBlindComparisonDialog}
            onRunBlindComparison={(params) => {
              if (selectedEvalForBlindComparison) {
                runBlindComparisonEval({
                  conversationEvalId: selectedEvalForBlindComparison.id,
                  ...params,
                }).catch((e: unknown) => {
                  console.error('Failed to run blind comparison eval:', e);
                });
                handleCloseBlindComparisonDialog();
              }
            }}
          />
        </DialogContent>
      </Dialog>
      {/* Single Model Dialog */}
      <Dialog open={openSingleModelDialog} onClose={handleCloseSingleModelDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Evaluación de Modelo Único</DialogTitle>
        <DialogContent>
          {selectedEvalForSingleModel && (
            <SingleModelForm
              conversationEval={selectedEvalForSingleModel}
              onClose={handleCloseSingleModelDialog}
              onRunEvaluation={(params) => {
                if (selectedEvalForSingleModel) {
                  runSingleModelEval({
                    conversationEvalId: selectedEvalForSingleModel.id,
                    targetModel: params.targetModel,
                    evaluationModel: params.evaluationModel,
                    threshold: params.threshold,
                    businessUnitIds: selectedEvalForSingleModel.businessUnitIds,
                  }).catch((e: unknown) => {
                    console.error('Failed to run single model eval:', e);
                  });
                  handleCloseSingleModelDialog();
                }
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ConversationEvals;
